import { ApiError, patientsApi, type PatientResponse } from '../lib/api'

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://127.0.0.1:8000/api/v1')

/**
 * Provider API client — wires the provider experience to the real backend
 * routers documented in docs/design/provider-backend-api-2026-08-30.md.
 *
 * No fabricated fallback content anywhere in this module: failures surface as
 * thrown ApiError values that the UI turns into honest error/empty states.
 */

export type ProviderRosterRow = {
  patient_id: string
  person_id: string
  nhi: string
  mrn: string
  name_ar: string
  name_latin: string
  first_name: string
  last_name: string
  dob: string
  sex: 'M' | 'F' | '—'
  phone: string
  address: Record<string, string> | null
  facility_id: string
  verification_tier: 1 | 2 | 3 | 4
  status: string
  registration_source: string | null
  updated_at: string | null
  medications_count: number
  family_history_record_count: number
}

export type ProviderRosterResponse = {
  items: ProviderRosterRow[]
  total: number
}

export type PatientUpdatePayload = {
  name_ar?: string
  name_latin?: string
  dob?: string
  sex?: 'M' | 'F'
  phone?: string
  address?: Record<string, string>
  facility_id?: string
  mrn?: string
  verification_tier?: number
  status?: string
  registration_source?: string | null
}

/* ------- Medications (real router: /api/v1, see apps/api/app/api/v1/medications.py) ------- */

export type MedicationStatement = {
  id: string
  patient_id: string
  reported_by: 'patient' | 'clinician'
  description: string
  rxnorm_code: string | null
  status: string
  date_recorded: string
  source: string | null
  note: string | null
  dose: string | null
  dose_unit: string | null
  route: string | null
  form: string | null
  frequency_code: string | null
  duration: string | null
  quantity: string | null
  refills: number | null
  indication: string | null
  prescriber_id: string | null
  pharmacy_formulary_notes: string | null
  dispense_instructions: string | null
  confidence_tier: number
  created_at?: string | null
  updated_at?: string | null
}

export type MedicationListResponse = {
  items: MedicationStatement[]
  total: number
}

export type MedicationCreatePayload = {
  patient_id: string
  reported_by: 'patient' | 'clinician'
  description: string
  rxnorm_code?: string | null
  status?: string
  date_recorded: string
  source?: string | null
  note?: string | null
  dose?: string | null
  dose_unit?: string | null
  route?: string | null
  form?: string | null
  frequency_code?: string | null
  duration?: string | null
  quantity?: string | null
  refills?: number | null
  indication?: string | null
  confidence_tier?: number
}

/* ------- Family history (real router: /api/v1/family-history) ------- */

export type FamilyHistoryCondition = {
  id: string
  family_history_relative_id: string
  snomed_code: string
  name: string
  age_at_onset: number | null
  last_confirmed_at: string | null
  needs_reconfirmation: boolean
  confirmed_until: string | null
}

export type FamilyHistoryRelative = {
  id: string
  patient_id: string
  relationship: string
  relationship_code: string | null
  relationship_label: string | null
  relative_name: string | null
  relative_birth_year: number | null
  deceased: boolean
  age_at_death: number | null
  certainty: string
  record_status: string
  notes: string | null
  conditions: FamilyHistoryCondition[]
  needs_reconfirmation: boolean
}

export type FamilyHistoryListResponse = {
  items: FamilyHistoryRelative[]
  total: number
}

export type FamilyHistoryRelativeCreatePayload = {
  relationship: string
  relative_name?: string | null
  relative_birth_year?: number | null
  deceased?: boolean
  age_at_death?: number | null
  certainty?: string
  record_status?: string
  notes?: string | null
  conditions: { snomed_code: string; name: string; age_at_onset?: number | null }[]
}

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers })
  if (!response.ok) {
    let detail: unknown = response.statusText
    try {
      const body = await response.json()
      detail = body.detail ?? body
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, detail)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * Live roster search — GET /api/v1/provider/roster?q=&limit=.
 *
 * The roster endpoint performs server-side ILIKE filtering across NHI, MRN,
 * names, phone and DOB; the client sends the raw query and renders whatever
 * comes back (including an empty list, which the UI turns into a real empty
 * state).
 */
export async function searchProviderRoster(
  query: string,
  options: { limit?: number; signal?: AbortSignal } = {},
): Promise<ProviderRosterRow[]> {
  const params = new URLSearchParams()
  if (query.trim()) params.set('q', query.trim())
  params.set('limit', String(options.limit ?? 50))
  const response = await requestJson<ProviderRosterResponse>(
    `/provider/roster?${params.toString()}`,
    { signal: options.signal },
  )
  return response.items
}

/** Live patient record — shared by the detail panel and the edit form. */
export async function getProviderPatient(
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<PatientResponse> {
  return requestJson<PatientResponse>(`/patients/${patientId}`, { signal: options.signal })
}

/**
 * PATCH /api/v1/patients/{id} with partial (changed-fields-only) semantics.
 * Returns the server-persisted PatientResponse; callers must reflect this
 * response, not local-only state.
 */
export async function updateProviderPatient(
  patientId: string,
  payload: PatientUpdatePayload,
): Promise<PatientResponse> {
  return requestJson<PatientResponse>(`/patients/${patientId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

/* ------- Medications CRUD ------- */

export async function listPatientMedications(
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<MedicationListResponse> {
  return requestJson<MedicationListResponse>(`/patients/${patientId}/medications`, {
    signal: options.signal,
  })
}

export async function createMedication(payload: MedicationCreatePayload): Promise<MedicationStatement> {
  return requestJson<MedicationStatement>('/medications', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function deleteMedication(medicationId: string): Promise<void> {
  return requestJson<void>(`/medications/${medicationId}`, { method: 'DELETE' })
}

/* ------- Family history CRUD ------- */

export async function listFamilyHistory(
  patientId: string,
  options: { signal?: AbortSignal } = {},
): Promise<FamilyHistoryListResponse> {
  return requestJson<FamilyHistoryListResponse>(`/family-history/${patientId}`, {
    signal: options.signal,
  })
}

export async function addFamilyHistoryRelative(
  patientId: string,
  payload: FamilyHistoryRelativeCreatePayload,
): Promise<FamilyHistoryRelative> {
  return requestJson<FamilyHistoryRelative>(`/family-history/${patientId}/relative`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function deleteFamilyHistoryRelative(relativeId: string): Promise<void> {
  return requestJson<void>(`/family-history/relative/${relativeId}`, { method: 'DELETE' })
}

// Re-export for existing imports that resolved ApiError through this module.
export { ApiError, patientsApi }
